/**
 * CarryoverIndicator — shared "↳ X.XX lbs carryover" line with hover tooltip
 * listing each source session's contribution.
 *
 * Used by:
 *   - Waste Summary step (mobile wizard)
 *   - Session Details modal → Details tab (Supervisor)
 *
 * Renders nothing when the allocations sum is 0.
 */
import React from 'react';
import { useLanguage } from '../i18n/LanguageContext';

export interface CarryoverAllocation {
  allocated_weight: number | string;
  breakdown?: Array<{
    weight: number;
    source_session_number: string | null;
    source_sub_session_number?: string | null;
  }>;
}

interface Props {
  allocations: CarryoverAllocation[];
  className?: string;
  tooltipAlign?: 'left' | 'right';
}

const CarryoverIndicator: React.FC<Props> = ({
  allocations,
  className = '',
  tooltipAlign = 'left',
}) => {
  const { t } = useLanguage();

  const total = allocations.reduce(
    (sum, a) => sum + (Number(a.allocated_weight) || 0),
    0
  );
  if (total <= 0) return null;

  const lbsFromSessionLabel = t('mobile.lbsFromSession') || 'lbs from session';
  const lines = allocations.flatMap(a =>
    (a.breakdown || []).map(
      b =>
        `${Number(b.weight).toFixed(2)} ${lbsFromSessionLabel} ${
          b.source_session_number || b.source_sub_session_number || '?'
        }`
    )
  );

  const alignClass = tooltipAlign === 'right' ? 'right-0' : 'left-0';

  return (
    <div className={className}>
      <div className="relative inline-block group">
        <span className="text-xs text-gray-500 dark:text-gray-400 cursor-help underline decoration-dotted underline-offset-2 whitespace-nowrap">
          ↳ {total.toFixed(2)} {t('mobile.lbsCarryover') || 'lbs carryover'}
        </span>
        <div className={`invisible group-hover:visible absolute ${alignClass} bottom-full mb-1 z-50 px-3 py-2 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded shadow-lg whitespace-nowrap pointer-events-none`}>
          {lines.map((line, i) => <div key={i}>{line}</div>)}
        </div>
      </div>
    </div>
  );
};

export default CarryoverIndicator;
